import { Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { SelectChangeEvent } from '@mui/material/Select';
import moment, { Moment } from 'moment';
import { ChangeEvent, FC, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auctionApi } from 'src/api/auctionApi';
import { circleApi, TribeAccountType } from 'src/api/circles';
import { couponApi } from 'src/api/coupons';
import { UploadImage } from 'src/components/common/UploadImage/UploadImage';
import { VideoUpload } from 'src/components/common/VideoUpload/VideoUpload';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';

import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import Members from '../../components/common/CircleMembersCount/Members';
import { ApplyCoupon } from './ApplyCoupon/ApplyCoupon';
import { AuctionSize } from './Auction/AuctionSize/AuctionSize';
import { AuctionTime } from './Auction/AuctionTime/AuctionTime';
import { CircleOptions } from './CircleOptions/CircleOptions';
import { CommonField } from './CommonField/CommonField';
import styles from './CreateCircle.module.scss';
import { Header } from './Header/Header';
import { ProceedModal } from './ProceedModal/ProceedModal';
import { DESCRIPTION_FIELD, NAME_FIELD } from './const';

export interface FormData {
  tribe_name: string;
  welcome_video_id: string;
  description: string;
  member_count_max: number;
  account_type: string;
  coupon_code?: string;
  special_code?: string;
  tribe_avatar_url: string;
  tribe_avatar_id?: string;
}

export const CreateCircle: FC = () => {
  const [seats, setSeats] = useState(0);

  const nameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  const [serverError, setServerError] = useState<string | null>(null);
  const [duration, setDuration] = useState<string>('148');
  const [auctionSize, setAuctionSize] = useState<string>('20');
  const [startDate] = useState<Moment>(moment());
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof FormData, string>>
  >({});

  const [seatsAnother, setSeatsAnother] = useState(0);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    tribe_name: '',
    welcome_video_id: '',
    description: '',
    member_count_max: 1,
    account_type: 'FIXED',
    coupon_code: '',
    special_code: '',
    tribe_avatar_url: '',
    tribe_avatar_id: '',
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleInputChange = useCallback(
    (field: keyof FormData) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prevData) => ({
          ...prevData,
          [field]: event.target.value,
        }));
        setFieldErrors((prevErrors) => {
          const { [field]: removedError, ...restErrors } = prevErrors;
          return restErrors;
        });
      },
    []
  );

  const handleImageUpdate = (fileId: string | File) => {
    if (typeof fileId === 'string') {
      setFormData((prev) => ({
        ...prev,
        tribe_avatar_id: fileId,
      }));

      setFieldErrors((prevErrors) => {
        const { tribe_avatar_id, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  };

  const handleVideoUpdate = (fileId: string | File) => {
    if (typeof fileId === 'string') {
      setFormData((prev) => ({
        ...prev,
        welcome_video_id: fileId,
      }));

      setFieldErrors((prevErrors) => {
        const { welcome_video_id, ...restErrors } = prevErrors;
        return restErrors;
      });
    }
  };

  const handleSelectChange = useCallback(
    (field: keyof FormData) => (event: SelectChangeEvent) => {
      setFormData((prevData) => ({
        ...prevData,
        [field]: Number(event.target.value),
      }));
    },
    []
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.tribe_name.trim()) {
      newErrors.tribe_name = 'Circle name is required';
    }
    if (!formData.welcome_video_id) {
      newErrors.welcome_video_id = 'Video is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (formData.account_type === 'FIXED' && formData.member_count_max <= 0) {
      newErrors.member_count_max = 'Member count must be greater than 0.';
    }
    if (!formData.tribe_avatar_id) {
      newErrors.tribe_avatar_id = 'Photo is required';
    }

    setFieldErrors(newErrors);

    const firstErrorKey = Object.keys(newErrors)[0] as
      keyof FormData | undefined;
    if (firstErrorKey) {
      const scrollMap: Partial<
        Record<keyof FormData, React.RefObject<HTMLDivElement>>
      > = {
        tribe_name: nameRef,
        welcome_video_id: videoRef,
        description: descriptionRef,
        member_count_max: membersRef,
        tribe_avatar_id: imageRef,
      };

      const targetRef = scrollMap[firstErrorKey];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const createCircle = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    setServerError(null);
    setLoading(true);
    const finalize = () => setLoading(false);

    const handleError = (err: any) => {
      console.error('Full Error Object:', err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      console.error('Error:', message);
      setServerError(message);
      finalize();
    };

    if (formData.coupon_code?.length === 12) {
      couponApi
        .getCouponByCode(formData.coupon_code)
        .then((coupon) => {
          setSeats(coupon.max_users ?? 0);
          setSeatsAnother(coupon.used_count_total ?? 0);
          if (coupon.max_users < formData.member_count_max) {
            setIsOpenModal(true);
            finalize();
          } else {
            circleApi
              .createCircle(formData)
              .then((res) => {
                dispatch(fetchLoggedUser());
                setTimeout(() => {
                  navigate(PathRoutes.ProfileRoute);
                  finalize();
                }, 3000);
              })
              .catch(handleError);
          }
        })
        .catch(handleError);
    } else if (formData.account_type === 'AUCTION') {
      circleApi
        .createCircle(formData)
        .then((res) => {
          const calculatedEndDate = moment(startDate).add(
            Number(duration) ?? 0,
            'minutes'
          );

          auctionApi
            .createAuction(res._id, {
              open_seat_count: Number(auctionSize),
              openDate: startDate.toDate(),
              closeDate: calculatedEndDate.toDate(),
            })
            .then((result) => {
              const tribeId = result.tribe_id;
              const auctionLink = `/circle/${tribeId}`;
              dispatch(fetchLoggedUser());
              setTimeout(() => {
                navigate(auctionLink, { replace: true });
                finalize();
              }, 3000);
            })
            .catch(handleError);
        })
        .catch(handleError);
    } else {
      circleApi
        .createCircle(formData)
        .then((res) => {
          dispatch(fetchLoggedUser());
          setTimeout(() => {
            if (res.payment_link) {
              window.location.href = res.payment_link;
            }
            finalize();
          }, 3000);
        })
        .catch(handleError);
    }
  }, [
    formData,
    validateForm,
    navigate,
    duration,
    startDate,
    auctionSize,
    dispatch,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Header />
        <div ref={nameRef}>
          <CommonField
            title={NAME_FIELD.title}
            description={NAME_FIELD.description}
            placeholder={NAME_FIELD.placeholder}
            charactersLength={NAME_FIELD.charactersLength}
            anchor={'info-name'}
            value={formData.tribe_name}
            setValue={handleInputChange('tribe_name')}
          />
          {fieldErrors.tribe_name && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
            >
              {fieldErrors.tribe_name}
            </Typography>
          )}
        </div>

        <div ref={videoRef}>
          <VideoUpload
            videoType="video-welcome"
            description={`Similar to the Description, this is your chance to pitch the purpose and value of your Circle.`}
            error={fieldErrors.welcome_video_id}
            onUpdate={handleVideoUpdate}
          />
        </div>

        <div ref={imageRef}>
          <UploadImage
            onUpdate={handleImageUpdate}
            defaultImage={
              typeof formData.tribe_avatar_url === 'string'
                ? formData.tribe_avatar_url
                : ''
            }
          >
            Add Photo
          </UploadImage>
        </div>
        {fieldErrors.tribe_avatar_id && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
          >
            {fieldErrors.tribe_avatar_id}
          </Typography>
        )}
        <div ref={descriptionRef}>
          <CommonField
            type="textarea"
            title={DESCRIPTION_FIELD.title}
            description={DESCRIPTION_FIELD.description}
            placeholder={DESCRIPTION_FIELD.placeholder}
            charactersLength={DESCRIPTION_FIELD.charactersLength}
            anchor={'info-description'}
            value={formData.description}
            setValue={handleInputChange('description')}
          />
          {fieldErrors.description && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 0.5, ml: 1, fontWeight: 700 }}
            >
              {fieldErrors.description}
            </Typography>
          )}
        </div>
        <CommonField
          title="Special code (optional)"
          charactersLength={12}
          value={formData.special_code ?? ''}
          setValue={handleInputChange('special_code')}
        />

        <CircleOptions
          membershipType={formData.account_type}
          setMembershipType={(type) =>
            setFormData((prev) => ({
              ...prev,
              account_type: type,
              coupon_code: type === 'AUCTION' ? '' : prev.coupon_code,
            }))
          }
        />
        {formData.account_type === 'FIXED' && (
          <>
            <Members
              value={formData.member_count_max}
              accountType={formData.account_type as TribeAccountType}
              setValue={handleSelectChange('member_count_max')}
            />
            <ApplyCoupon
              value={formData.coupon_code}
              setValue={handleInputChange('coupon_code')}
            />
          </>
        )}
        {formData.account_type === 'AUCTION' && (
          <>
            <AuctionTime duration={duration} setDuration={setDuration} />
            <AuctionSize size={auctionSize} setSize={setAuctionSize} />
          </>
        )}
        <button
          onClick={createCircle}
          className={styles.btn}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Create Circle'
          )}
        </button>
        {serverError && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mt: 2, fontWeight: 700, textAlign: 'center' }}
          >
            {serverError}
          </Typography>
        )}

        <ProceedModal
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          seats={seats}
          formData={formData}
          seatsAnother={seatsAnother}
        />
      </div>
    </div>
  );
};
